import React, { useEffect, useMemo, useState } from "react";
import LegalLinks from "./LegalLinks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getSolidDataset,
  getThing,
  getThingAll,
  createThing,
  setThing,
  saveSolidDatasetAt,
  getStringNoLocale,
  getUrl,
  getUrlAll,
  removeAll,
  setStringNoLocale,
  setUrl,
  addUrl,
  createContainerAt,
  getSolidDatasetWithAcl,
  getResourceAcl,
  hasResourceAcl,
  hasAccessibleAcl,
  createAclFromFallbackAcl,
  setPublicResourceAccess,
  saveAclFor,
  overwriteFile,
} from "@inrupt/solid-client";
import { FOAF, VCARD, LDP } from "@inrupt/vocab-common-rdf";
import {
  faFlask,
  faHexagonNodes,
  faSitemap,
} from "@fortawesome/free-solid-svg-icons";
import session from "../solidSession";
import {
  buildDefaultPrivateRegistry,
  ensureCatalogStructure,
  ensurePrivateRegistryContainer,
  loadRegistryConfig,
  loadRegistryMembersFromContainer,
  REGISTRY_PRESETS,
  resolveCatalogUrl,
  saveRegistryConfig,
  SDP_CATALOG,
  LEGACY_SDP_CATALOG,
} from "../solidCatalog";
import { ensurePodStorageLink } from "../solidStorage";
import {
  hasStatisticsConsent,
  saveStatisticsConsent,
} from "../statisticsConsent";
import ContentLoadingState from "./ContentLoadingState";
import "./OnboardingWizard.css";

const VCARD_TYPE = "http://www.w3.org/2006/vcard/ns#type";

const REGISTRY_ICON_MAP = {
  flask: faFlask,
};

const guessContentType = (filename, fallback = "application/octet-stream") => {
  const ext = filename.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "svg":
      return "image/svg+xml";
    default:
      return fallback;
  }
};

const getProfileDocUrl = (webId) => (webId ? webId.split("#")[0] : "");

const getPodRoot = (webId) => {
  const url = new URL(webId);
  const segments = url.pathname.split("/").filter(Boolean);
  const profileIndex = segments.indexOf("profile");
  const baseSegments = profileIndex > -1 ? segments.slice(0, profileIndex) : segments;
  const basePath = baseSegments.length ? `/${baseSegments.join("/")}/` : "/";
  return `${url.origin}${basePath}`;
};

const normalizeEmails = (values) =>
  values
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

const containerExists = async (url) => {
  if (!url) return false;
  try {
    await getSolidDataset(url, { fetch: session.fetch });
    return true;
  } catch {
    return false;
  }
};

export default function OnboardingWizard({
  webId,
  statisticsConfig,
  onComplete,
  onCancel,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");

  const [dataset, setDataset] = useState(null);
  const [profileThing, setProfileThing] = useState(null);
  const [profileDocUrl, setProfileDocUrl] = useState("");

  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [role, setRole] = useState("");
  const [emails, setEmails] = useState([""]);
  const [inboxUrl, setInboxUrl] = useState("");
  const [photoIri, setPhotoIri] = useState("");
  const [photoSrc, setPhotoSrc] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [inboxAcknowledged, setInboxAcknowledged] = useState(false);
  const [catalogUrl, setCatalogUrl] = useState("");
  const [catalogAcknowledged, setCatalogAcknowledged] = useState(false);
  const [registryMode, setRegistryMode] = useState("private");
  const [registrySelections, setRegistrySelections] = useState([]);
  const [researchRegistryAcknowledged, setResearchRegistryAcknowledged] = useState(false);
  const [privateRegistryUrl, setPrivateRegistryUrl] = useState("");
  const [privateRegistryAcknowledged, setPrivateRegistryAcknowledged] = useState(false);
  const [statisticsAcknowledged, setStatisticsAcknowledged] = useState(false);

  const steps = useMemo(
    () => [
      { id: 1, title: "Basics" },
      { id: 2, title: "Email" },
      { id: 3, title: "Pod setup" },
    ],
    []
  );

  const basicsComplete = Boolean(name.trim());
  const emailsComplete = normalizeEmails(emails).length > 0;
  const statisticsEnabled = Boolean(statisticsConfig?.enabled);
  const defaultPrivateRegistry = buildDefaultPrivateRegistry(webId);
  const registrySelectionsComplete = registrySelections.length > 0;
  const getRegistryConfig = () => ({
    mode: registryMode === "private" ? "private" : "research",
    registries: registrySelections,
    privateRegistry: privateRegistryUrl || defaultPrivateRegistry,
  });
  const toggleRegistrySelection = (url) => {
    setRegistrySelections((prev) =>
      prev.includes(url) ? prev.filter((item) => item !== url) : [...prev, url]
    );
  };
  const loadProfile = async () => {
    if (!webId) return;
    setLoading(true);
    setError("");
    try {
      const profileDoc = getProfileDocUrl(webId);
      setProfileDocUrl(profileDoc);
      const ds = await getSolidDataset(profileDoc, { fetch: session.fetch });
      setDataset(ds);
      let me = getThing(ds, webId) || getThingAll(ds).find((t) => t.url === webId);
      if (!me) me = createThing({ url: webId });
      setProfileThing(me);

      const nm =
        getStringNoLocale(me, VCARD.fn) ||
        getStringNoLocale(me, FOAF.name) ||
        `${getStringNoLocale(me, VCARD.given_name) || ""} ${getStringNoLocale(me, VCARD.family_name) || ""}`.trim();
      const loadedOrg = getStringNoLocale(me, VCARD.organization_name) || "";
      const loadedRole = getStringNoLocale(me, VCARD.role) || "";
      setName(nm || "");
      setOrg(loadedOrg);
      setRole(loadedRole);

      const emailUris = getUrlAll(me, VCARD.hasEmail) || [];
      const collected = [];
      emailUris.forEach((uri) => {
        if (uri.startsWith("mailto:")) {
          collected.push(uri.replace(/^mailto:/, ""));
        } else {
          const thing = getThing(ds, uri);
          if (thing) {
            const email = (getUrl(thing, VCARD.value) || "").replace(/^mailto:/, "");
            if (email) collected.push(email);
          }
        }
      });

      const directEmails = (getUrlAll(me, VCARD.email) || []).map((uri) =>
        uri.replace(/^mailto:/, "")
      );
      const allEmails = [...collected, ...directEmails].filter(Boolean);
      setEmails(allEmails.length ? allEmails : [""]);

      const inbox = getUrl(me, LDP.inbox) || "";
      const targetInboxUrl = `${getPodRoot(webId)}inbox/`;
      const hasInboxContainer = await containerExists(targetInboxUrl);
      setInboxUrl(inbox);
      setInboxAcknowledged(hasInboxContainer);
      const photo = getUrl(me, VCARD.hasPhoto) || getUrl(me, FOAF.img) || "";
      setPhotoIri(photo);
      const statisticsConsentGranted =
        !statisticsEnabled || hasStatisticsConsent(me);
      setStatisticsAcknowledged(statisticsConsentGranted);

      const profileCatalog = getUrl(me, SDP_CATALOG) || getUrl(me, LEGACY_SDP_CATALOG) || "";
      let catalogResolved = profileCatalog;
      let hasCatalog = false;
      const catalogContainerUrl = `${getPodRoot(webId)}catalog/`;
      const hasCatalogContainer = await containerExists(catalogContainerUrl);
      if (profileCatalog) {
        try {
          await getSolidDataset(profileCatalog.split("#")[0], { fetch: session.fetch });
          hasCatalog = true;
        } catch {
          hasCatalog = false;
        }
      } else {
        try {
          catalogResolved = await resolveCatalogUrl(webId, session.fetch);
        } catch {
          catalogResolved = "";
        }
      }
      setCatalogUrl(catalogResolved);
      setCatalogAcknowledged(hasCatalogContainer);

      const registryConfig = await loadRegistryConfig(webId, session.fetch);
      const hasResearchSelection = (registryConfig.registries || []).length > 0;
      const nextMode =
        registryConfig.mode === "private" || hasResearchSelection
          ? registryConfig.mode
          : "private";
      setRegistryMode(nextMode || "private");
      setRegistrySelections(registryConfig.registries || []);
      const resolvedPrivateRegistry =
        registryConfig.privateRegistry || buildDefaultPrivateRegistry(webId);
      setPrivateRegistryUrl(resolvedPrivateRegistry);
      const hasPrivateRegistryContainer = await containerExists(resolvedPrivateRegistry);
      let hasResearchRegistryMembership = false;
      if ((registryConfig.registries || []).length) {
        const membershipChecks = await Promise.all(
          (registryConfig.registries || []).map(async (registryUrl) => {
            const members = await loadRegistryMembersFromContainer(registryUrl, session.fetch);
            return members.includes(webId);
          })
        );
        hasResearchRegistryMembership = membershipChecks.some(Boolean);
      }
      setResearchRegistryAcknowledged(hasResearchRegistryMembership);
      setPrivateRegistryAcknowledged(hasPrivateRegistryContainer);

      const missingBasics = !nm;
      const missingEmail = allEmails.length === 0;
      const missingInbox = !inbox;
      const missingCatalog = !hasCatalog;
      const registryMissing =
        !((registryConfig.privateRegistry || buildDefaultPrivateRegistry(webId)).trim()) ||
        (registryConfig.mode !== "private" && !(registryConfig.registries || []).length);
      const missingStatisticsConsent =
        statisticsEnabled && !statisticsConsentGranted;

      if (
        !missingBasics &&
        !missingEmail &&
        !missingInbox &&
        !missingCatalog &&
        !registryMissing &&
        !missingStatisticsConsent
      ) {
        onComplete();
        return;
      }

      if (missingBasics) setStep(1);
      else if (missingEmail) setStep(2);
      else setStep(3);
    } catch (err) {
      console.error("Failed to load profile:", err);
      setError("Failed to load profile information.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [webId]);

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        if (!photoIri) {
          setPhotoSrc("");
          return;
        }
        const res = await session.fetch(photoIri);
        if (!res.ok) throw new Error(`Avatar ${res.status}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setPhotoSrc(objectUrl);
      } catch {
        setPhotoSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoIri]);

  useEffect(() => {
    if (registryMode !== "private") return;
    if (!privateRegistryUrl && webId) {
      setPrivateRegistryUrl(buildDefaultPrivateRegistry(webId));
    }
  }, [registryMode, privateRegistryUrl, webId]);

  const uploadAvatar = async (file) => {
    const podRoot = getPodRoot(webId);

    const ensureContainer = async (containerUrl) => {
      try {
        await getSolidDataset(containerUrl, { fetch: session.fetch });
      } catch (e) {
        if (e?.statusCode === 404 || e?.response?.status === 404) {
          await createContainerAt(containerUrl, { fetch: session.fetch });
        } else {
          throw e;
        }
      }
    };

    const profileUrl = `${podRoot}profile/`;
    await ensureContainer(profileUrl);

    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const targetUrl = `${profileUrl}avatar-${Date.now()}.${ext}`;
    await overwriteFile(targetUrl, file, {
      contentType: file.type || guessContentType(file.name, "image/*"),
      fetch: session.fetch,
    });
    return targetUrl;
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setError("");
    try {
      const url = await uploadAvatar(file);
      setPhotoIri(url);
    } catch (err) {
      console.error("Avatar upload failed:", err);
      setError("Avatar upload failed. Please try again.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const saveBasics = async () => {
    if (!dataset || !profileDocUrl) return;
    let me = profileThing || createThing({ url: webId });
    me = removeAll(me, VCARD.fn);
    me = removeAll(me, VCARD.organization_name);
    me = removeAll(me, VCARD.role);
    me = setStringNoLocale(me, VCARD.fn, name.trim());
    if (org.trim()) {
      me = setStringNoLocale(me, VCARD.organization_name, org.trim());
    }
    if (role.trim()) {
      me = setStringNoLocale(me, VCARD.role, role.trim());
    }
    me = removeAll(me, VCARD.hasPhoto);
    if (photoIri) {
      me = setUrl(me, VCARD.hasPhoto, photoIri);
    }
    me = ensurePodStorageLink(me, webId);

    const updated = setThing(dataset, me);
    await saveSolidDatasetAt(profileDocUrl, updated, { fetch: session.fetch });
    setDataset(updated);
    setProfileThing(me);
  };

  const saveEmails = async () => {
    if (!dataset || !profileDocUrl) return;
    let me = profileThing || createThing({ url: webId });
    let ds = dataset;

    me = removeAll(me, VCARD.hasEmail);
    me = removeAll(me, VCARD.email);

    const list = normalizeEmails(emails);
    list.forEach((email, idx) => {
      const nodeUrl = `${profileDocUrl}#email-${Date.now()}-${idx}`;
      let emailNode = createThing({ url: nodeUrl });
      emailNode = removeAll(emailNode, VCARD.value);
      emailNode = setUrl(emailNode, VCARD.value, `mailto:${email}`);
      emailNode = setStringNoLocale(emailNode, VCARD_TYPE, "Work");
      ds = setThing(ds, emailNode);
      me = addUrl(me, VCARD.hasEmail, nodeUrl);
    });

    ds = setThing(ds, me);
    await saveSolidDatasetAt(profileDocUrl, ds, { fetch: session.fetch });
    setDataset(ds);
    setProfileThing(me);
  };

  const getResourceAndAcl = async (url) => {
    const resource = await getSolidDatasetWithAcl(url, { fetch: session.fetch });
    let resourceAcl;
    if (!hasResourceAcl(resource)) {
      if (!hasAccessibleAcl(resource)) {
        throw new Error("No access to ACL.");
      }
      resourceAcl = createAclFromFallbackAcl(resource);
    } else {
      resourceAcl = getResourceAcl(resource);
    }
    return { resource, resourceAcl };
  };

  const configureInbox = async () => {
    if (!webId || !profileDocUrl) return;
    const targetInboxUrl = `${getPodRoot(webId)}inbox/`;
    try {
      await getSolidDataset(targetInboxUrl, { fetch: session.fetch });
    } catch (e) {
      if (e?.statusCode === 404 || e?.response?.status === 404) {
        await createContainerAt(targetInboxUrl, { fetch: session.fetch });
      } else {
        throw e;
      }
    }

    const { resource, resourceAcl } = await getResourceAndAcl(targetInboxUrl);
    const updatedAcl = setPublicResourceAccess(resourceAcl, {
      read: false,
      append: true,
      write: false,
      control: false,
    });
    await saveAclFor(resource, updatedAcl, { fetch: session.fetch });

    let ds = dataset;
    if (!ds) {
      ds = await getSolidDataset(profileDocUrl, { fetch: session.fetch });
    }
    let me = profileThing || createThing({ url: webId });
    me = removeAll(me, LDP.inbox);
    me = setUrl(me, LDP.inbox, targetInboxUrl);
    ds = setThing(ds, me);
    await saveSolidDatasetAt(profileDocUrl, ds, { fetch: session.fetch });
    setDataset(ds);
    setProfileThing(me);
    setInboxUrl(targetInboxUrl);
  };

  const configureCatalog = async () => {
    if (!webId) return;
    const title = name ? `${name}'s Catalog` : "Solid Dataspace Catalog";
    const registryConfig = getRegistryConfig();
    await saveRegistryConfig(webId, session.fetch, registryConfig);
    await ensurePrivateRegistryContainer(
      webId,
      session.fetch,
      registryConfig.privateRegistry
    );
    const { catalogUrl: configuredUrl } = await ensureCatalogStructure(webId, session.fetch, {
      title,
      registryConfig,
    });
    setCatalogUrl(configuredUrl);
  };

  const handleNext = async () => {
    setError("");
    setSaving(true);
    try {
      if (step === 1) {
        await saveBasics();
        setStep(2);
      } else if (step === 2) {
        await saveEmails();
        setStep(3);
      } else if (step === 3) {
        if (statisticsEnabled && !statisticsAcknowledged) {
          throw new Error("Usage statistics consent is required.");
        }
        await configureInbox();
        await configureCatalog();
        if (statisticsEnabled) {
          await saveStatisticsConsent(webId, session.fetch);
        }
        onComplete();
      }
    } catch (err) {
      console.error("Setup step failed:", err);
      setError(err?.message || "Saving failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep((prev) => prev - 1);
  };

  if (loading) {
    return (
      <div className="onboarding-wrap">
        <ContentLoadingState
          title="Profile setup"
          message="Loading your personal dataspace workspace …"
          icon={faHexagonNodes}
        />
      </div>
    );
  }

  return (
    <div className="onboarding-wrap">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <div>
            <div className="onboarding-title">Welcome to the Solid Dataspace</div>
            <div className="onboarding-subtitle">
              Complete these steps to activate your dataspace access.
            </div>
          </div>
          <button className="onboarding-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>

        <div className="onboarding-steps">
          {steps.map((item) => (
            <div
              key={item.id}
              className={`onboarding-step ${item.id === step ? "active" : ""} ${
                item.id < step ? "done" : ""
              }`}
            >
              <div className="onboarding-step__index">{item.id}</div>
              <div className="onboarding-step__label">{item.title}</div>
            </div>
          ))}
        </div>

        {error && <div className="onboarding-error">{error}</div>}

        {step === 1 && (
          <div className="onboarding-section">
            <h3>Basics</h3>
            <p>Please provide your profile basics.</p>
            <div className="onboarding-basics">
              <div className="onboarding-avatar">
                <label
                  className={`onboarding-avatar__upload${photoUploading ? " is-uploading" : ""}`}
                  aria-label="Upload profile photo"
                  title="Upload profile photo (optional)"
                >
                  {photoSrc ? (
                    <img src={photoSrc} alt="Profile avatar" />
                  ) : (
                    <div className="onboarding-avatar__placeholder">No photo</div>
                  )}
                  <span className="onboarding-avatar__overlay" aria-hidden="true">
                    <svg
                      className="onboarding-avatar__icon"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M9 4h6l1.2 2H20a2 2 0 0 1 2 2v9a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V8a2 2 0 0 1 2-2h3.8L9 4Zm3 5a5 5 0 1 0 0 10a5 5 0 0 0 0-10Zm0 2a3 3 0 1 1 0 6a3 3 0 0 1 0-6Z"
                      />
                    </svg>
                    {photoUploading ? (
                      <span className="onboarding-avatar__uploading">Uploading...</span>
                    ) : null}
                  </span>
                  <input type="file" accept="image/*" hidden onChange={onPickAvatar} />
                </label>
              </div>
              <div className="onboarding-grid">
                <div>
                  <label htmlFor="onboarding-name">Name</label>
                  <input
                    id="onboarding-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="onboarding-organization">Organization (optional)</label>
                  <input
                    id="onboarding-organization"
                    type="text"
                    value={org}
                    onChange={(e) => setOrg(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="onboarding-role">Role (optional)</label>
                  <input
                    id="onboarding-role"
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-section">
            <h3>Email</h3>
            <p>Add at least one contact email.</p>
            <div className="onboarding-list">
              {emails.map((email, idx) => (
                <div className="onboarding-list__row" key={idx}>
                  <input
                    type="email"
                    placeholder="name@example.org"
                    value={email}
                    onChange={(e) => {
                      const next = [...emails];
                      next[idx] = e.target.value;
                      setEmails(next);
                    }}
                  />
                  <button
                    type="button"
                    className="onboarding-remove"
                    onClick={() => {
                      const next = emails.filter((_, i) => i !== idx);
                      setEmails(next.length ? next : [""]);
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="onboarding-add"
                onClick={() => setEmails([...emails, ""])}
              >
                Add email
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-section">
            <h3>Inbox, Catalog & Registry</h3>
            <p>
              Configure your shared Solid inbox, catalog, and registry settings so access
              requests and metadata can work with your pod.
            </p>
            <div className="onboarding-inbox">
              <div className="onboarding-inbox__label">Inbox URL</div>
              <div className="onboarding-inbox__value">
                {inboxUrl || `${getPodRoot(webId)}inbox/`}
              </div>
              <div className="onboarding-inbox__hint">
                The inbox will be created in a <code>inbox/</code> container in your pod.
              </div>
              <label className="onboarding-checkbox">
                <input
                  type="checkbox"
                  checked={inboxAcknowledged}
                  onChange={(e) => setInboxAcknowledged(e.target.checked)}
                />
                <span>I understand that finishing will create and configure my inbox.</span>
              </label>
            </div>

            <div className="onboarding-inbox" style={{ marginTop: 18 }}>
              <div className="onboarding-inbox__label">Catalog URL</div>
              <div className="onboarding-inbox__value">
                {catalogUrl || `${getPodRoot(webId)}catalog/cat.ttl#it`}
              </div>
              <div className="onboarding-inbox__hint">
                The catalog metadata will be created in a <code>catalog/</code> container in your pod.
              </div>
              <label className="onboarding-checkbox">
                <input
                  type="checkbox"
                  checked={catalogAcknowledged}
                  onChange={(e) => setCatalogAcknowledged(e.target.checked)}
                />
                <span>I understand that finishing will create and configure my catalog.</span>
              </label>
            </div>

            <div className="onboarding-inbox" style={{ marginTop: 18 }}>
              <div className="onboarding-inbox__label">Registry Mode</div>
              <div className="onboarding-inbox__hint">
                Choose whether you want to participate in a research registry or keep a private
                registry inside your pod.
              </div>
              <div className="onboarding-toggle" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className={`onboarding-toggleBtn ${registryMode === "research" ? "active" : ""}`}
                  onClick={() => setRegistryMode("research")}
                >
                  Research project
                </button>
                <button
                  type="button"
                  className={`onboarding-toggleBtn ${registryMode === "private" ? "active" : ""}`}
                  onClick={() => setRegistryMode("private")}
                >
                  Private use
                </button>
              </div>
            </div>

            {registryMode === "research" && (
              <div className="onboarding-inbox" style={{ marginTop: 18 }}>
                <div className="onboarding-inbox__label">Research registries</div>
                <div className="onboarding-registryGrid">
                  {REGISTRY_PRESETS.map((preset) => {
                    const selected = registrySelections.includes(preset.url);
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        className={`onboarding-registryCard ${selected ? "selected" : ""}`}
                        onClick={() => toggleRegistrySelection(preset.url)}
                        aria-pressed={selected}
                        title={preset.label}
                      >
                        <div className="onboarding-registryImage">
                          {preset.logo ? (
                            <img src={preset.logo} alt={preset.label} loading="lazy" />
                          ) : (
                            <FontAwesomeIcon
                              icon={REGISTRY_ICON_MAP[preset.icon] || faSitemap}
                              className="onboarding-registryIcon"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <span className="onboarding-registryCheck" aria-hidden="true">&#10003;</span>
                      </button>
                    );
                  })}
                </div>
                <div className="onboarding-inbox__hint">Select at least one registry.</div>
                <label className="onboarding-checkbox">
                  <input
                    type="checkbox"
                    checked={researchRegistryAcknowledged}
                    onChange={(e) => setResearchRegistryAcknowledged(e.target.checked)}
                  />
                  <span>
                    I understand that finishing will register me in the selected registries.
                  </span>
                </label>
              </div>
            )}
            <div className="onboarding-inbox" style={{ marginTop: 18 }}>
              <div className="onboarding-inbox__label">Private Registry</div>
              <div className="onboarding-inbox__value">
                {privateRegistryUrl || defaultPrivateRegistry}
              </div>
              <div className="onboarding-inbox__hint">
                The private registry will always be created in your pod root under{" "}
                <code>registry/</code>.
              </div>
              <label className="onboarding-checkbox">
                <input
                  type="checkbox"
                  checked={privateRegistryAcknowledged}
                  onChange={(e) => setPrivateRegistryAcknowledged(e.target.checked)}
                />
                <span>I understand that finishing will create and configure my registry.</span>
              </label>
            </div>

            {statisticsEnabled && (
              <div className="onboarding-inbox" style={{ marginTop: 18 }}>
                <div className="onboarding-inbox__label">Usage Statistics</div>
                <div className="onboarding-inbox__value">
                  {`${getPodRoot(webId)}${
                    statisticsConfig?.activityResourcePath ||
                    "statistics/activity/activity.ttl"
                  }`}
                </div>
                <div className="onboarding-inbox__hint">
                  The Manager stores your last activity time and active calendar month
                  in this file. The statistics worker receives read-only access.
                </div>
                <label className="onboarding-checkbox">
                  <input
                    type="checkbox"
                    checked={statisticsAcknowledged}
                    onChange={(event) =>
                      setStatisticsAcknowledged(event.target.checked)
                    }
                  />
                  <span>
                    I agree that finishing will create and update my usage statistics
                    in my pod.
                  </span>
                </label>
              </div>
            )}

          </div>
        )}

        <div className="onboarding-actions">
          <button
            type="button"
            className="onboarding-back"
            onClick={handleBack}
            disabled={step === 1 || saving}
          >
            Back
          </button>
          <button
            type="button"
            className="onboarding-next"
            onClick={handleNext}
            disabled={
              saving ||
              (step === 1 && !basicsComplete) ||
              (step === 2 && !emailsComplete) ||
              (step === 3 &&
                (!inboxAcknowledged ||
                  !catalogAcknowledged ||
                  !privateRegistryAcknowledged ||
                  (statisticsEnabled && !statisticsAcknowledged) ||
                  (registryMode === "research" &&
                    (!researchRegistryAcknowledged || !registrySelectionsComplete))))
            }
          >
            {saving ? "Saving..." : step === 3 ? "Finish" : "Next"}
          </button>
        </div>
        <footer className="onboarding-legal"><LegalLinks /></footer>
      </div>
    </div>
  );
}
